/**
 * Drawn shapes as the person handles them (`Form` in shared/doc.ts): the few points they move —
 * corners, and round points the outline flows through — or an ellipse, and the exact Bézier path
 * made from them.
 *
 * Round points follow the Curvature tool's rule: the outline passes through a round point along
 * the circle through it and its two neighbours. Three points on a circle therefore give that very
 * circle, and a round point between two corners gives the one arc through all three. Between two
 * corners an edge is straight. Arcs are drawn as cubics of at most a quarter turn (within 0.03 %
 * of the true circle), so every curve is as round as a compass would draw it.
 *
 * A corner may be rounded (`r`, its radius): the outline leaves each side where the circle of that
 * radius touching both sides does, and follows it round — a rounded rectangle's corner, a curb
 * return. Where two roundings would overlap on a side too short for both, both give way alike.
 */
import type { Form, Vec } from '../../shared/doc.ts';
import type { CurvePath } from './curve.ts';
import { flattenPath } from './curve.ts';

export type PolyForm = Extract<Form, { f: 'poly' }>;
export type RectForm = Extract<Form, { f: 'rect' }>;
export type EllipseForm = Extract<Form, { f: 'ellipse' }>;

/** One cubic from the point before it. */
export interface Cubic {
  c1: Vec;
  c2: Vec;
  to: Vec;
}

/** Handle length of a quarter circle drawn as one cubic, per unit of radius. */
const KAPPA = 0.5522847498307936;
/** Two semi-axes this close (relative) are one circle: dragged near it, an ellipse becomes the circle. */
export const CIRCLE_DETENT = 0.035;

const v = (x: number, y: number): Vec => ({ x, y });
const add = (a: Vec, b: Vec): Vec => v(a.x + b.x, a.y + b.y);
const sub = (a: Vec, b: Vec): Vec => v(a.x - b.x, a.y - b.y);
const mul = (a: Vec, k: number): Vec => v(a.x * k, a.y * k);
const dot = (a: Vec, b: Vec): number => a.x * b.x + a.y * b.y;
const cross = (a: Vec, b: Vec): number => a.x * b.y - a.y * b.x;
const len = (a: Vec): number => Math.hypot(a.x, a.y);
const unit = (a: Vec): Vec => {
  const l = len(a) || 1;
  return v(a.x / l, a.y / l);
};
const rot = (a: Vec, t: number): Vec => {
  const c = Math.cos(t),
    s = Math.sin(t);
  return v(a.x * c - a.y * s, a.x * s + a.y * c);
};
const angleBetween = (a: Vec, b: Vec): number => Math.atan2(cross(a, b), dot(a, b));

/* ------------------------------------------------------------------ points */

export function polyPoints(f: PolyForm): Vec[] {
  const out: Vec[] = [];
  for (let i = 0; i + 1 < f.pts.length; i += 2) out.push(v(f.pts[i]!, f.pts[i + 1]!));
  return out;
}

/** An ellipse's handles: the ends of its axes, in order round it (0 and 2 on rx, 1 and 3 on ry). */
export function ellipseHandles(e: EllipseForm): Vec[] {
  const c = v(e.cx, e.cy);
  return [0, 1, 2, 3].map((k) => add(c, rot(v(k % 2 ? 0 : e.rx * (k === 0 ? 1 : -1), k % 2 ? e.ry * (k === 1 ? 1 : -1) : 0), e.rot)));
}

/** A box's corners, in order round it: top left, top right, bottom right, bottom left (in its own turn). */
export function rectCorners(r: RectForm): Vec[] {
  const c = v(r.cx, r.cy);
  return [v(-1, -1), v(1, -1), v(1, 1), v(-1, 1)].map((s) => add(c, rot(v((s.x * r.w) / 2, (s.y * r.h) / 2), r.rot)));
}

/** Where a form's point handles are: a poly's points, a box's corners, an ellipse's axis ends. */
export function formHandles(f: Form): Vec[] {
  return f.f === 'poly' ? polyPoints(f) : f.f === 'rect' ? rectCorners(f) : ellipseHandles(f);
}

/**
 * Where a turning knob belongs: the middle of a box's top side, or the top end of an oval (a
 * circle has none), with the way out from there (the knob stands a little way off along it).
 */
export function formKnob(f: Form): { at: Vec; out: Vec } | null {
  if (f.f === 'poly' || (f.f === 'ellipse' && isCircle(f))) return null;
  const half = f.f === 'rect' ? f.h / 2 : f.ry;
  const out = rot(v(0, -1), f.rot);
  return { at: add(v(f.cx, f.cy), mul(out, half)), out };
}

export function isCircle(e: EllipseForm): boolean {
  return Math.abs(e.rx - e.ry) <= 1e-6 * Math.max(e.rx, e.ry);
}

/* ---------------------------------------------------------------- tangents */

/**
 * The outline's direction through each round point: along the circle through it and its
 * neighbours, in the order of travel (null at a corner). Three points in a line: along the line —
 * unless the outline turns right back there (the point beyond the next, a hairpin: no circle and no
 * one way through it), when it is a corner.
 */
function tangents(P: Vec[], round: readonly number[]): (Vec | null)[] {
  const n = P.length;
  return P.map((b, i) => {
    if (!round[i]) return null;
    const a = P[(i - 1 + n) % n]!,
      c = P[(i + 1) % n]!;
    const turn = cross(sub(b, a), sub(c, b));
    const span = len(sub(c, a));
    const o = circumcentre(a, b, c);
    // (nearly) in a line: straight on through the point — or, turning right back, a corner
    if (!o || Math.abs(turn) < 1e-9 * span * span) return dot(sub(b, a), sub(c, b)) < 0 ? null : unit(sub(c, a));
    const r = sub(b, o);
    return unit(mul(v(-r.y, r.x), Math.sign(turn)));
  });
}

function circumcentre(a: Vec, b: Vec, c: Vec): Vec | null {
  const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
  const scale = Math.max(len(sub(b, a)), len(sub(c, b)), len(sub(c, a)));
  if (Math.abs(d) < 1e-9 * scale * scale) return null;
  const a2 = a.x * a.x + a.y * a.y,
    b2 = b.x * b.x + b.y * b.y,
    c2 = c.x * c.x + c.y * c.y;
  return v((a2 * (b.y - c.y) + b2 * (c.y - a.y) + c2 * (a.y - b.y)) / d, (a2 * (c.x - b.x) + b2 * (a.x - c.x) + c2 * (b.x - a.x)) / d);
}

/* ----------------------------------------------------------------- an edge */

/**
 * The cubics from A to B. Without a direction at either end the edge is straight; with one, it is
 * the circular arc leaving (or arriving) that way; with two, the arc when they agree, else the
 * smooth curve inscribed in the triangle of their lines.
 */
export function edgeCubics(A: Vec, B: Vec, tA: Vec | null, tB: Vec | null): Cubic[] {
  const chord = sub(B, A);
  const L = len(chord);
  if (L < 1e-9 || (!tA && !tB)) return [{ c1: add(A, mul(chord, 1 / 3)), c2: sub(B, mul(chord, 1 / 3)), to: B }];
  const u = mul(chord, 1 / L);
  const mirror = (t: Vec): Vec => sub(mul(u, 2 * dot(t, u)), t);
  const ta = tA ?? mirror(tB!),
    tb = tB ?? mirror(tA!);
  const fa = angleBetween(u, ta),
    fb = angleBetween(u, tb);
  if (Math.abs(fa + fb) < 1e-3) {
    // the two ends agree on one circle: the arc itself (straight when there is none to speak of: no
    // turn, or a whole turn — leaving A straight away from B, no circle that way comes back through it)
    if (Math.abs(fa) < 1e-5 || Math.abs(Math.sin((fb - fa) / 2)) < 1e-6) return [{ c1: add(A, mul(chord, 1 / 3)), c2: sub(B, mul(chord, 1 / 3)), to: B }];
    return arcCubics(A, B, ta, fb - fa);
  }
  const turn = angleBetween(ta, tb);
  const tc = cross(ta, tb);
  let ha = -1,
    hb = -1;
  if (Math.abs(tc) > 1e-6 && Math.abs(turn) < 2.6) {
    // their lines meet at C (ahead of A, behind B): the curve a circle would take in that corner
    const s = cross(chord, tb) / tc;
    const w = cross(chord, ta) / tc;
    if (s > 0 && w < 0) {
      const k = ((4 / 3) * Math.tan(Math.abs(turn) / 4)) / Math.tan(Math.abs(turn) / 2);
      ha = Math.min(s * k, L);
      hb = Math.min(-w * k, L);
    }
  }
  if (ha < 0) {
    const a = (Math.abs(fa) + Math.abs(fb)) / 2;
    ha = hb = Math.min(L / (3 * Math.cos(a / 2) ** 2), L);
  }
  return [{ c1: add(A, mul(ta, ha)), c2: sub(B, mul(tb, hb)), to: B }];
}

/** The circular arc from A leaving along tA and turning by `turn` (radians) to reach B, in quarter turns at most. */
function arcCubics(A: Vec, B: Vec, tA: Vec, turn: number): Cubic[] {
  const L = len(sub(B, A));
  const R = L / (2 * Math.abs(Math.sin(turn / 2)));
  const sg = Math.sign(turn);
  const o = add(A, mul(v(-tA.y, tA.x), R * sg));
  const n = Math.max(1, Math.ceil(Math.abs(turn) / (Math.PI / 2) - 1e-9));
  const step = turn / n;
  const h = (4 / 3) * R * Math.tan(Math.abs(step) / 4);
  const out: Cubic[] = [];
  let p = A,
    t = tA;
  for (let k = 1; k <= n; k++) {
    const q = k === n ? B : add(o, rot(sub(A, o), k * step));
    const tq = rot(tA, k * step);
    out.push({ c1: add(p, mul(t, h)), c2: sub(q, mul(tq, h)), to: q });
    p = q;
    t = tq;
  }
  return out;
}

/* ------------------------------------------------------------------ a form */

/** A run of the outline: where it starts, and its cubics. */
interface Run {
  from: Vec;
  cubics: Cubic[];
}

/** A shape of corners as a custom shape (a box's corners, with its roundings), or null for an ellipse. */
export function cornered(f: Form): PolyForm | null {
  if (f.f === 'poly') return f;
  if (f.f === 'ellipse') return null;
  const out: PolyForm = { f: 'poly', pts: rectCorners(f).flatMap((p) => [p.x, p.y]), round: [0, 0, 0, 0] };
  if (f.r && f.r.some((x) => x > 0)) out.r = f.r.slice();
  return out;
}
/** Whether a shape has a rounded corner. */
function rounded(f: Form): boolean {
  return f.f !== 'ellipse' && !!f.r && f.r.some((x, i) => x > 0 && (f.f === 'rect' || !f.round[i]));
}

/** The cubics of each edge, in order: edge i leaves point i (an ellipse's edges leave its handles). */
export function formEdges(f: Form): Cubic[][] {
  if (f.f === 'poly' || rounded(f)) return polyRuns(cornered(f)!).edges.map((e) => e.cubics);
  if (f.f === 'rect') {
    const P = rectCorners(f);
    return P.map((a, i) => edgeCubics(a, P[(i + 1) % 4]!, null, null));
  }
  const c = v(f.cx, f.cy);
  const at = (t: number): Vec => add(c, rot(v(f.rx * Math.cos(t), f.ry * Math.sin(t)), f.rot));
  const d = (t: number): Vec => rot(v(-f.rx * Math.sin(t), f.ry * Math.cos(t)), f.rot);
  return [0, 1, 2, 3].map((k) => {
    const t0 = (k * Math.PI) / 2,
      t1 = ((k + 1) * Math.PI) / 2;
    return [{ c1: add(at(t0), mul(d(t0), KAPPA)), c2: sub(at(t1), mul(d(t1), KAPPA)), to: at(t1) }];
  });
}

/** A corner's rounding as asked (0: sharp; a round point has none). */
export function radiusOf(f: PolyForm | RectForm, i: number): number {
  return f.f === 'poly' && f.round[i] ? 0 : (f.r?.[i] ?? 0);
}

/**
 * A custom shape's outline, run by run: before each point, its rounding (null when it has none),
 * then the edge from it on to the next point — trimmed where the roundings take over.
 */
function polyRuns(f: PolyForm): { edges: Run[]; fillets: (Run | null)[] } {
  const P = polyPoints(f);
  const n = P.length;
  const T = tangents(P, f.round);
  const base: Run[] = P.map((a, i) => ({ from: a, cubics: edgeCubics(a, P[(i + 1) % n]!, T[i]!, T[(i + 1) % n]!) }));
  const fillets: (Run | null)[] = P.map(() => null);
  if (!f.r || !f.r.some((x, i) => x > 0 && !f.round[i])) return { edges: base, fillets };
  // how far along each side a rounding reaches from its corner: r / tan(half the corner's angle)
  const reach = P.map((_, i) => {
    const r = radiusOf(f, i);
    if (!(r > 0)) return 0;
    const dIn = endDir(base[(i - 1 + n) % n]!),
      dOut = startDir(base[i]!);
    const turn = Math.abs(angleBetween(dIn, dOut));
    if (turn < 0.5 * DEG || turn > 179 * DEG) return 0;
    return r / Math.tan((Math.PI - turn) / 2);
  });
  // two roundings on a side too short for both give way alike (each keeps the lesser share)
  const L = base.map(runLength);
  const give = base.map((_, j) => {
    const want = reach[j]! + reach[(j + 1) % n]!;
    return want > 0.98 * L[j]! ? (0.98 * L[j]!) / want : 1;
  });
  const d = reach.map((x, i) => x * Math.min(give[i]!, give[(i - 1 + n) % n]!));
  const edges = base.map((e, j) => trimRun(e, d[j]!, d[(j + 1) % n]!));
  P.forEach((_, i) => {
    if (!(d[i]! > 1e-9)) return;
    const before = edges[(i - 1 + n) % n]!,
      after = edges[i]!;
    const A = runEnd(before);
    fillets[i] = { from: A, cubics: edgeCubics(A, after.from, endDir(before), startDir(after)) };
  });
  return { edges, fillets };
}

const DEG = Math.PI / 180;

function runEnd(r: Run): Vec {
  return r.cubics.length ? r.cubics[r.cubics.length - 1]!.to : r.from;
}
/** The way a run leaves its start. */
function startDir(r: Run): Vec {
  const c = r.cubics[0];
  if (!c) return v(1, 0);
  for (const q of [c.c1, c.c2, c.to]) if (len(sub(q, r.from)) > 1e-9) return unit(sub(q, r.from));
  return v(1, 0);
}
/** The way a run arrives at its end. */
function endDir(r: Run): Vec {
  const k = r.cubics.length - 1;
  const c = r.cubics[k];
  if (!c) return v(1, 0);
  const p0 = k ? r.cubics[k - 1]!.to : r.from;
  for (const q of [c.c2, c.c1, p0]) if (len(sub(c.to, q)) > 1e-9) return unit(sub(c.to, q));
  return v(1, 0);
}
function cubicLength(p0: Vec, c: Cubic, steps = 16): number {
  let l = 0,
    prev = p0;
  for (let k = 1; k <= steps; k++) {
    const q = cubicAt(p0, c, k / steps);
    l += len(sub(q, prev));
    prev = q;
  }
  return l;
}
function runLength(r: Run): number {
  let l = 0,
    p0 = r.from;
  for (const c of r.cubics) {
    l += cubicLength(p0, c);
    p0 = c.to;
  }
  return l;
}
/** A cubic split at t: the part before and the part after. */
function splitCubic(p0: Vec, c: Cubic, t: number): [Cubic, Cubic] {
  const lerp = (a: Vec, b: Vec) => add(a, mul(sub(b, a), t));
  const a = lerp(p0, c.c1),
    b = lerp(c.c1, c.c2),
    d = lerp(c.c2, c.to);
  const e = lerp(a, b),
    g = lerp(b, d);
  const m = lerp(e, g);
  return [
    { c1: a, c2: e, to: m },
    { c1: g, c2: d, to: c.to },
  ];
}
/** Where along a run (cubic k, parameter t) its length from the start reaches s. */
function runParam(r: Run, s: number): { k: number; t: number } {
  let p0 = r.from;
  for (let k = 0; k < r.cubics.length; k++) {
    const c = r.cubics[k]!;
    const l = cubicLength(p0, c);
    if (s <= l || k === r.cubics.length - 1) {
      // within this cubic: by its own length table (a straight side's parameter is its length)
      const steps = 32;
      let acc = 0,
        prev = p0;
      for (let j = 1; j <= steps; j++) {
        const q = cubicAt(p0, c, j / steps);
        const dl = len(sub(q, prev));
        if (acc + dl >= s) return { k, t: Math.min(1, (j - 1 + (dl > 1e-12 ? (s - acc) / dl : 0)) / steps) };
        acc += dl;
        prev = q;
      }
      return { k, t: 1 };
    }
    s -= l;
    p0 = c.to;
  }
  return { k: 0, t: 0 };
}
/** A run with `a` of its length taken off its start and `b` off its end. */
function trimRun(r: Run, a: number, b: number): Run {
  let out = r;
  if (b > 1e-9) {
    const { k, t } = runParam(out, runLength(out) - b);
    const p0 = k ? out.cubics[k - 1]!.to : out.from;
    const [left] = splitCubic(p0, out.cubics[k]!, t);
    out = { from: out.from, cubics: [...out.cubics.slice(0, k), left] };
  }
  if (a > 1e-9) {
    const { k, t } = runParam(out, a);
    const p0 = k ? out.cubics[k - 1]!.to : out.from;
    const [left, right] = splitCubic(p0, out.cubics[k]!, t);
    out = { from: left.to, cubics: [right, ...out.cubics.slice(k + 1)] };
  }
  return out;
}

/** A custom shape's outline in runs, for drawing part of it (a shape being placed, its closing side apart): each edge (edge i leaves point i), and each point's rounding. */
export function formRuns(f: PolyForm): { edges: { from: Vec; cubics: Cubic[] }[]; fillets: ({ from: Vec; cubics: Cubic[] } | null)[] } {
  return polyRuns(f);
}

/** The whole outline as one closed run: its start, and every cubic round it in order. */
function formLoop(f: Form): Run {
  if (f.f !== 'poly' && !rounded(f)) {
    return { from: formHandles(f)[0]!, cubics: formEdges(f).flat() };
  }
  const { edges, fillets } = polyRuns(cornered(f)!);
  const cubics: Cubic[] = [];
  edges.forEach((e, i) => {
    const fl = fillets[i];
    if (fl) cubics.push(...fl.cubics);
    cubics.push(...e.cubics);
  });
  return { from: fillets[0]?.from ?? edges[0]!.from, cubics };
}

/** The closed Bézier path of a form, in the document's storage (anchors, and in/out handles per anchor). */
export function formPath(f: Form): CurvePath {
  const loop = formLoop(f);
  const start = loop.from;
  const anchors: Vec[] = [start];
  const ins: Vec[] = [start];
  const outs: Vec[] = [];
  for (const c of loop.cubics) {
    outs[anchors.length - 1] = c.c1;
    anchors.push(c.to);
    ins.push(c.c2);
  }
  // the last cubic arrives back at the start
  anchors.pop();
  ins[0] = ins.pop()!;
  const pts: number[] = [],
    h: number[] = [];
  anchors.forEach((a, i) => {
    pts.push(a.x, a.y);
    const o = outs[i] ?? a;
    h.push(ins[i]!.x, ins[i]!.y, o.x, o.y);
  });
  return { pts, h };
}

/** The outline as a polygon (for hit tests, cuts and joins). */
export function formPolygon(f: Form, maxSeg = 5): number[] {
  const p = formPath(f);
  return flattenPath(p.pts, p.h, maxSeg);
}

function cubicAt(p0: Vec, c: Cubic, t: number): Vec {
  const s = 1 - t;
  return v(s * s * s * p0.x + 3 * s * s * t * c.c1.x + 3 * s * t * t * c.c2.x + t * t * t * c.to.x, s * s * s * p0.y + 3 * s * s * t * c.c1.y + 3 * s * t * t * c.c2.y + t * t * t * c.to.y);
}

/** The middle of a run, on it (the pieces of an arc turn equally: its middle is where the middle piece is half way). */
function runMid(r: Run): Vec {
  const e = r.cubics;
  if (!e.length) return r.from;
  const k = Math.floor((e.length - 1) / 2);
  const p0 = k === 0 ? r.from : e[k - 1]!.to;
  return e.length % 2 ? cubicAt(p0, e[k]!, 0.5) : e[k]!.to;
}

/** The middle of each edge, on the outline (where a new point is pulled out of it; a rounded corner's side, between its roundings). */
export function formMids(f: Form): Vec[] {
  if (f.f === 'poly' || rounded(f)) return polyRuns(cornered(f)!).edges.map(runMid);
  const starts = formHandles(f);
  return formEdges(f).map((cubics, i) => runMid({ from: starts[i]!, cubics }));
}

/**
 * A corner's rounding as it stands: its point, the way into the corner along the middle of its
 * angle (`b`, unit), the sine of half its angle, the radius it has (after giving way on a short
 * side), the largest it could have, and the middle of its arc (the point itself when sharp). Null
 * for a round point, or a corner too nearly straight (or folded back) to round.
 */
export function cornerRounding(form: PolyForm | RectForm, i: number): { p: Vec; b: Vec; sinHalf: number; r: number; maxR: number; mid: Vec } | null {
  const f = cornered(form)!;
  if (f.round[i]) return null;
  const P = polyPoints(f);
  const n = P.length;
  const T = tangents(P, f.round);
  const into = { from: P[(i - 1 + n) % n]!, cubics: edgeCubics(P[(i - 1 + n) % n]!, P[i]!, T[(i - 1 + n) % n]!, T[i]!) };
  const out = { from: P[i]!, cubics: edgeCubics(P[i]!, P[(i + 1) % n]!, T[i]!, T[(i + 1) % n]!) };
  const dIn = endDir(into),
    dOut = startDir(out);
  const turn = Math.abs(angleBetween(dIn, dOut));
  if (turn < 0.5 * DEG || turn > 179 * DEG) return null;
  const half = (Math.PI - turn) / 2;
  const b = unit(add(mul(dIn, -1), dOut));
  const tanHalf = Math.tan(half);
  // the largest: its reach using the whole of the shorter side, less what the next rounding takes of it
  const runs = polyRuns({ ...f, r: (f.r ?? P.map(() => 0)).map((x, k) => (k === i ? 0 : x)) });
  const avail = Math.min(runLength(runs.edges[(i - 1 + n) % n]!), runLength(runs.edges[i]!));
  const maxR = 0.98 * avail * tanHalf;
  const fl = polyRuns(f).fillets[i];
  const p = P[i]!;
  if (!fl) return { p, b, sinHalf: Math.sin(half), r: 0, maxR, mid: p };
  const d = len(sub(fl.from, p));
  return { p, b, sinHalf: Math.sin(half), r: d * tanHalf, maxR, mid: runMid(fl) };
}

/** Corner i rounded to radius r (0: sharp again). */
export function setRounding<F extends PolyForm | RectForm>(f: F, i: number, r: number): F;
export function setRounding(f: PolyForm | RectForm, i: number, r: number): PolyForm | RectForm {
  if (f.f === 'rect') {
    const rs = (f.r ?? [0, 0, 0, 0]).slice();
    rs[i] = Math.max(0, r);
    const { r: _was, ...box } = f;
    return rs.some((x) => x > 0) ? { ...box, r: rs } : box;
  }
  const rs = (f.r ?? f.round.map(() => 0)).slice();
  rs[i] = f.round[i] ? 0 : Math.max(0, r);
  const out: PolyForm = { f: 'poly', pts: f.pts.slice(), round: f.round.slice() };
  if (rs.some((x) => x > 0)) out.r = rs;
  return out;
}

/** What a point is: a corner, a rounded corner, or a point the outline curves through. */
export type PointKind = 'corner' | 'rounded' | 'curve';
export function pointKind(f: PolyForm, i: number): PointKind {
  return f.round[i] ? 'curve' : radiusOf(f, i) > 0 ? 'rounded' : 'corner';
}
/** Point i made a kind; a rounded corner is rounded to `r` (or keeps the rounding it has). */
export function setPointKind(f: PolyForm, i: number, kind: PointKind, r: number): PolyForm {
  const round = f.round.slice();
  round[i] = kind === 'curve' ? 1 : 0;
  const g: PolyForm = { f: 'poly', pts: f.pts.slice(), round, ...(f.r ? { r: f.r.slice() } : {}) };
  return setRounding(g, i, kind === 'rounded' ? radiusOf(f, i) || r : 0);
}

/* ----------------------------------------------------------------- editing */

export function translateForm(f: Form, dx: number, dy: number): Form {
  if (f.f !== 'poly') return { ...f, cx: f.cx + dx, cy: f.cy + dy };
  const pts = f.pts.slice();
  for (let i = 0; i + 1 < pts.length; i += 2) {
    pts[i] = pts[i]! + dx;
    pts[i + 1] = pts[i + 1]! + dy;
  }
  return withRadii({ f: 'poly', pts, round: f.round.slice() }, f.r);
}

/**
 * Handle i carried to p (`min`: the smallest a box or ellipse may get, image px). A poly's point
 * goes there. A box's corner goes there with the opposite corner staying put — the box resizes,
 * keeping its turn, and never turns inside out; within a hair of square it is square. An
 * ellipse keeps its centre: a circle's radius is the finger's distance (the handle stays under
 * the finger), an oval's carried axis takes the finger's reach along it; an oval carried to
 * within a hair of round becomes a circle.
 */
export function moveHandle(f: Form, i: number, p: Vec, min = 1): Form {
  if (f.f === 'poly') {
    const pts = f.pts.slice();
    pts[2 * i] = p.x;
    pts[2 * i + 1] = p.y;
    return withRadii({ f: 'poly', pts, round: f.round.slice() }, f.r);
  }
  if (f.f === 'rect') {
    const s = [v(-1, -1), v(1, -1), v(1, 1), v(-1, 1)][i]!;
    const opp = rectCorners(f)[(i + 2) % 4]!;
    const d = rot(sub(p, opp), -f.rot);
    let w = Math.max(min, s.x * d.x),
      h = Math.max(min, s.y * d.y);
    if (Math.abs(w - h) <= SQUARE_DETENT * Math.max(w, h)) w = h = Math.max(w, h);
    return { ...f, w, h, ...centreFrom(opp, v(s.x * w, s.y * h), f.rot) };
  }
  const d = sub(p, v(f.cx, f.cy));
  if (isCircle(f)) {
    const r = Math.max(min / 2, len(d));
    return { ...f, rx: r, ry: r, rot: Math.atan2(d.y, d.x) - (i * Math.PI) / 2 };
  }
  const axis = rot(i % 2 ? v(0, 1) : v(1, 0), f.rot);
  const r = Math.max(min / 2, Math.abs(dot(d, axis)));
  let rx = i % 2 ? f.rx : r,
    ry = i % 2 ? r : f.ry;
  if (Math.abs(rx - ry) <= CIRCLE_DETENT * Math.max(rx, ry)) rx = ry = r;
  return { ...f, rx, ry };
}

/** A box's centre from a fixed corner and the diagonal (in the box's own turn) to its opposite. */
function centreFrom(fixed: Vec, diag: Vec, turn: number): { cx: number; cy: number } {
  const c = add(fixed, rot(mul(diag, 0.5), turn));
  return { cx: c.x, cy: c.y };
}

/** Within this share of each other a box's sides are equal: a square. */
export const SQUARE_DETENT = 0.03;

/**
 * A box's side i (0 top, 1 right, 2 bottom, 3 left — the side leaving corner i) carried to p:
 * that side alone moves, square to itself; the opposite side stays; never closer than `min`.
 */
export function moveSide(r: RectForm, i: number, p: Vec, min = 1): RectForm {
  const q = rot(sub(p, v(r.cx, r.cy)), -r.rot);
  let x0 = -r.w / 2,
    x1 = r.w / 2,
    y0 = -r.h / 2,
    y1 = r.h / 2;
  if (i === 0) y0 = Math.min(q.y, y1 - min);
  else if (i === 1) x1 = Math.max(q.x, x0 + min);
  else if (i === 2) y1 = Math.max(q.y, y0 + min);
  else x0 = Math.min(q.x, x1 - min);
  const c = add(v(r.cx, r.cy), rot(v((x0 + x1) / 2, (y0 + y1) / 2), r.rot));
  return { ...r, cx: c.x, cy: c.y, w: x1 - x0, h: y1 - y0 };
}

/** A box or ellipse turned to `turn` radians about its centre. */
export function turnForm(f: Form, turn: number): Form {
  return f.f === 'poly' ? f : { ...f, rot: turn };
}

/**
 * A new point on edge i, at its middle, after point i: a corner on a straight edge, round where
 * the outline already curves.
 */
/**
 * A custom shape's side i (from point i to the next) carried to p by the handle in its middle: the
 * side moves square to itself, both its points together, so it stays parallel to where it was.
 */
export function moveEdge(f: PolyForm, i: number, p: Vec): PolyForm {
  const n = f.pts.length / 2;
  const j = (i + 1) % n;
  const a = v(f.pts[2 * i]!, f.pts[2 * i + 1]!),
    b = v(f.pts[2 * j]!, f.pts[2 * j + 1]!);
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  if (len < 1e-9) return f;
  const nx = -(b.y - a.y) / len,
    ny = (b.x - a.x) / len;
  const mid = formMids(f)[i]!;
  const d = (p.x - mid.x) * nx + (p.y - mid.y) * ny;
  const pts = f.pts.slice();
  pts[2 * i] = a.x + nx * d;
  pts[2 * i + 1] = a.y + ny * d;
  pts[2 * j] = b.x + nx * d;
  pts[2 * j + 1] = b.y + ny * d;
  return withRadii({ f: 'poly', pts, round: f.round.slice() }, f.r);
}

/** A form with these roundings (kept only when some corner has one). */
function withRadii(f: PolyForm, r: readonly number[] | undefined): PolyForm {
  return r && r.some((x) => x > 0) ? { ...f, r: r.slice() } : f;
}

export function insertPoint(f: PolyForm, i: number): PolyForm {
  const n = f.pts.length / 2;
  const at = formMids(f)[i]!;
  const round = f.round[i] || f.round[(i + 1) % n] ? 1 : 0;
  const pts = f.pts.slice();
  const rs = f.round.slice();
  pts.splice(2 * (i + 1), 0, at.x, at.y);
  rs.splice(i + 1, 0, round);
  const radii = f.r?.slice();
  radii?.splice(i + 1, 0, 0);
  return withRadii({ f: 'poly', pts, round: rs }, radii);
}

/** Without point i (a shape keeps three). */
export function removePoint(f: PolyForm, i: number): PolyForm | null {
  if (f.pts.length / 2 <= 3) return null;
  const pts = f.pts.slice();
  const rs = f.round.slice();
  pts.splice(2 * i, 2);
  rs.splice(i, 1);
  const radii = f.r?.slice();
  radii?.splice(i, 1);
  return withRadii({ f: 'poly', pts, round: rs }, radii);
}

/** A corner made round, or a round point made a corner (a rounded corner's rounding goes). */
export function toggleRound(f: PolyForm, i: number): PolyForm {
  const rs = f.round.slice();
  rs[i] = rs[i] ? 0 : 1;
  const radii = f.r?.slice();
  if (radii) radii[i] = 0;
  return withRadii({ f: 'poly', pts: f.pts.slice(), round: rs }, radii);
}

/** The ellipse's outline as a form of points (four round points on its axes: the same curve, now free to reshape). */
export function ellipseAsPoly(e: EllipseForm): PolyForm {
  const hs = ellipseHandles(e);
  return { f: 'poly', pts: hs.flatMap((p) => [p.x, p.y]), round: [1, 1, 1, 1] };
}

/* ------------------------------------------------------------- one kind to another */

export type FormKind = 'box' | 'circle' | 'oval' | 'custom';

/** What kind of shape a form is. */
export function kindOf(f: Form): FormKind {
  return f.f === 'rect' ? 'box' : f.f === 'ellipse' ? (isCircle(f) ? 'circle' : 'oval') : 'custom';
}

/**
 * A form made another kind, keeping its place and size: a box becomes the oval inside it or the
 * circle of its size, an ellipse the box round it, and anything its own points (a box's corners,
 * an ellipse's four round points). `outline` is the shape's outline, for a form that is none of
 * these (a poly), from which a box is the tightest one round it and an oval has its spread.
 */
export function convertForm(f: Form, to: FormKind, outline: number[]): Form {
  if (kindOf(f) === to) return f;
  if (to === 'custom') {
    if (f.f === 'rect') return cornered(f)!;
    if (f.f === 'ellipse') return ellipseAsPoly(f);
    return f;
  }
  if (to === 'box') {
    if (f.f === 'ellipse') return { f: 'rect', cx: f.cx, cy: f.cy, w: 2 * f.rx, h: 2 * f.ry, rot: f.rot };
    return boxAround(outline);
  }
  // round: the box's own frame, else the outline's spread
  const e: EllipseForm = f.f === 'rect' ? { f: 'ellipse', cx: f.cx, cy: f.cy, rx: f.w / 2, ry: f.h / 2, rot: f.rot } : f.f === 'ellipse' ? f : ovalOf(outline);
  if (to === 'oval') return isCircle(e) ? { ...e, rx: e.rx * 1.25, ry: e.ry / 1.25 } : e;
  const r = f.f === 'rect' ? (f.w + f.h) / 4 : Math.sqrt(e.rx * e.ry);
  return { f: 'ellipse', cx: e.cx, cy: e.cy, rx: r, ry: r, rot: 0 };
}

/**
 * The tightest box round an outline (flat x,y): turned the way the outline runs, and square to
 * the screen when that is within a few degrees of it.
 */
export function boxAround(outline: number[]): RectForm {
  const P: Vec[] = [];
  for (let i = 0; i + 1 < outline.length; i += 2) P.push(v(outline[i]!, outline[i + 1]!));
  const extent = (a: number) => {
    const c = Math.cos(a),
      s = Math.sin(a);
    let x0 = Infinity,
      x1 = -Infinity,
      y0 = Infinity,
      y1 = -Infinity;
    for (const q of P) {
      const x = q.x * c + q.y * s,
        y = -q.x * s + q.y * c;
      x0 = Math.min(x0, x);
      x1 = Math.max(x1, x);
      y0 = Math.min(y0, y);
      y1 = Math.max(y1, y);
    }
    return { x0, x1, y0, y1, area: (x1 - x0) * (y1 - y0) };
  };
  const DEG = Math.PI / 180;
  let best = 0,
    ba = Infinity;
  for (let d = 0; d < 90; d++) {
    const a = extent(d * DEG).area;
    if (a < ba) {
      ba = a;
      best = d * DEG;
    }
  }
  for (let d = -1; d <= 1; d += 0.1) {
    const a = extent(best + d * DEG).area;
    if (a < ba) {
      ba = a;
      best += d * DEG;
    }
  }
  const quarter = Math.PI / 2;
  const off = best - Math.round(best / quarter) * quarter;
  if (Math.abs(off) < 4 * DEG) best -= off;
  // keep it the way up it is most like (a turn under 45° either way)
  best -= Math.round(best / quarter) * quarter;
  const e = extent(best);
  const c = rot(v((e.x0 + e.x1) / 2, (e.y0 + e.y1) / 2), best);
  return { f: 'rect', cx: c.x, cy: c.y, w: Math.max(1, e.x1 - e.x0), h: Math.max(1, e.y1 - e.y0), rot: best };
}

/** The ellipse with an outline's area and spread (a filled ellipse's spread along an axis is a²/4). */
export function ovalOf(outline: number[]): EllipseForm {
  const n = outline.length / 2;
  let a = 0,
    sx = 0,
    sy = 0,
    sxx = 0,
    syy = 0,
    sxy = 0;
  const ox = outline[0] ?? 0,
    oy = outline[1] ?? 0;
  for (let i = 0; i < n; i++) {
    const px = outline[2 * i]! - ox,
      py = outline[2 * i + 1]! - oy;
    const qx = outline[(2 * i + 2) % (2 * n)]! - ox,
      qy = outline[(2 * i + 3) % (2 * n)]! - oy;
    const c = px * qy - qx * py;
    a += c;
    sx += (px + qx) * c;
    sy += (py + qy) * c;
    sxx += (px * px + px * qx + qx * qx) * c;
    syy += (py * py + py * qy + qy * qy) * c;
    sxy += (px * qy + 2 * px * py + 2 * qx * qy + qx * py) * c;
  }
  a /= 2;
  if (Math.abs(a) < 1e-9) return { f: 'ellipse', cx: ox, cy: oy, rx: 1, ry: 1, rot: 0 };
  const cx = sx / (6 * a),
    cy = sy / (6 * a);
  const cxx = sxx / (12 * a) - cx * cx,
    cyy = syy / (12 * a) - cy * cy,
    cxy = sxy / (24 * a) - cx * cy;
  const tr = (cxx + cyy) / 2;
  const det = Math.sqrt(Math.max(0, ((cxx - cyy) / 2) ** 2 + cxy * cxy));
  return { f: 'ellipse', cx: cx + ox, cy: cy + oy, rx: 2 * Math.sqrt(tr + det), ry: 2 * Math.sqrt(Math.max(1e-9, tr - det)), rot: 0.5 * Math.atan2(2 * cxy, cxx - cyy) };
}
